import { useEffect, useRef } from "react";
import { useVisibilityChange } from "./useVisibilityChange";

interface UseForegroundIntervalOptions {
  /** While false, the interval is not started. Default: true. */
  enabled?: boolean;
  /**
   * Fire the callback once immediately on mount, before the first interval tick.
   * Independent of `enabled` — the immediate fire happens even when the interval is disabled,
   * so this can replace a separate `useOnceWhen(true, callback)` call.
   */
  immediate?: boolean;
}

/**
 * Calls `callback` on a repeating interval while the document tab is in the foreground.
 * Pauses automatically when the tab is hidden; resumes when it becomes visible again.
 * Does NOT fire on the first interval tick unless `immediate` is set.
 * `callback` is stored in a ref so callers do not need to memoize it.
 */
export function useForegroundInterval(
  callback: () => void,
  intervalMs: number,
  options: UseForegroundIntervalOptions = {}
): void {
  const { enabled = true, immediate = false } = options;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // One-shot on mount, independent of the interval's enabled state. The hasFiredImmediate ref
  // prevents re-firing if immediate changes from false to true after mount.
  const hasFiredImmediate = useRef(false);
  useEffect(() => {
    if (immediate && !hasFiredImmediate.current) {
      hasFiredImmediate.current = true;
      callbackRef.current();
    }
  }, [immediate]);

  useVisibilityChange((state) => {
    if (!enabled) return;
    if (state === "visible") {
      if (intervalIdRef.current === null) {
        intervalIdRef.current = setInterval(() => callbackRef.current(), intervalMs);
      }
    } else {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }
  });

  useEffect(() => {
    if (!enabled) return;
    if (document.visibilityState === "visible") {
      intervalIdRef.current = setInterval(() => callbackRef.current(), intervalMs);
    }
    return () => {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [intervalMs, enabled]);
}
